import { useEffect } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Chip, Typography } from '@mui/material';

import { trackEvent } from '@services/tracking';

import { ColumnHeader, SimpleTable } from './components/SimpleTable';
import { SubscriptionStatus } from './components/SubscriptionStatus';
import {
  FiltersBar,
  SelectAdditionalLabel,
  SelectClassification,
  SelectSubscriptionStatus,
  SelectProduct,
} from './CrmFiltersBar';
import TOOLTIPS from './tooltips';
import { SourceClassification } from './source-classification.enum';

export type SubscriberDto = {
  id: number;
  name: string;
  email: string;
  isEmailVerified: boolean;
  instagramProfile: string;
  marketingConsent: boolean;
  status: string;
  product: string;
  classification: SourceClassification;
  renewsType: number; // Hmm, do we need this?
  purchasedAt: string;
  cancelledAt: string; // nullable
  expiresAt: string;
  labels: string[] | null;
};

const CLASSIFICATION_LABELS = {
  [SourceClassification.PaidSubscription]: 'Paid',
  // we treat GiftedAnnualSubscription as Paid subscription
  [SourceClassification.GiftedAnnualSubscription]: 'Paid',
  [SourceClassification.FreeSubscription]: 'Free',
  [SourceClassification.MigratedSubscription]: 'Migrated',
  [SourceClassification.BulkUpdateSubscription]: 'Bulk uploads',
  [SourceClassification.TrialSubscription]: 'Trial',
};

function createColumns() {
  // https://material-table.com/#/
  const columns = [
    {
      title: 'Name',
      field: 'name',
    },
    {
      title: <ColumnHeader label="Email" title={TOOLTIPS.email} />,
      render: (row: SubscriberDto) => (
        <div className="flex items-center">
          {row.isEmailVerified && row.marketingConsent ? (
            <Icon icon={faBadgeCheck} className="text-primary mr-2" />
          ) : null}
          <span className="select-all">{row.email}</span>
        </div>
      ),
    },
    {
      title: 'Instagram',
      render: (row: SubscriberDto) => (
        <div>
          {row.marketingConsent
            ? row.instagramProfile
            : row.instagramProfile?.replace(/./g, '*')}
        </div>
      ),
    },
    {
      title: <ColumnHeader label="Marketing" title={TOOLTIPS.marketing} />,
      render: (row: SubscriberDto) => (
        <div>{row.marketingConsent ? 'Yes' : 'No'}</div>
      ),
    },
    {
      title: <ColumnHeader label="Subscription" title={TOOLTIPS.status} />,
      render: (row: SubscriberDto) => (
        <div>
          <SubscriptionStatus subscriptionStatus={row.status} />
        </div>
      ),
    },
    {
      title: 'Product',
      field: 'product',
      render: (row: SubscriberDto) => (
        <div className="text-nowrap">{row.product}</div>
      ),
    },
    {
      title: (
        <ColumnHeader label="Classification" title={TOOLTIPS.classification} />
      ),
      field: 'classification',
      render: (row: SubscriberDto) => (
        <div>
          {CLASSIFICATION_LABELS[row.classification] || row.classification}
        </div>
      ),
    },
    {
      title: <ColumnHeader label="Purchased" title={TOOLTIPS.purchased} />,
      render: (row: SubscriberDto) => (
        <div>{new Date(row.purchasedAt).toLocaleDateString()}</div>
      ),
    },
    {
      title: <ColumnHeader label="Cancelled" title={TOOLTIPS.cancelled} />,
      render: (row: SubscriberDto) => (
        <div>
          {row.cancelledAt
            ? new Date(row.cancelledAt).toLocaleDateString()
            : '-'}
        </div>
      ),
    },
    {
      title: <ColumnHeader label="Renews" title={TOOLTIPS.renews} />,
      render: (row: SubscriberDto) => (
        <div className="whitespace-nowrap">
          {row.status === 'Inactive'
            ? 'expired on '
            : row.status === 'Cancelled'
            ? 'active till '
            : 'renews '}
          {new Date(row.expiresAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: <ColumnHeader label="Additional" title={TOOLTIPS.additional} />,
      render: (row: SubscriberDto) => (
        <div className="whitespace-nowrap">
          {row.labels?.filter(Boolean).map((label) => (
            <Chip
              key={row.id + label}
              label={label}
              className="rounded-full font-bold"
            />
          ))}
        </div>
      ),
    },
  ].map((c) => ({ ...c, sorting: false }));

  return columns;
}

export type SubscribedTableProps = {
  data: SubscriberDto[];
  totalResults: number;
  creator: {
    visibility: string;
  };
};
export function SubscribedTable({
  data,
  totalResults,
  ...props
}: SubscribedTableProps) {
  useEffect(() => {
    trackEvent({
      type: 'Viewed Subscribed Table',
      payload: {},
    });
  }, []);

  return (
    <div style={{ maxWidth: '100%', height: '100%' }}>
      <div>
        <FiltersBar totalResults={totalResults}>
          <div className="flex gap-4">
            <SelectProduct />
            <SelectClassification />
            <SelectSubscriptionStatus />
            <SelectAdditionalLabel />
          </div>
        </FiltersBar>
      </div>
      <div className="my-3 text-sm text-[#8A8E99]">
        <Typography>
          Contact information is shown for all users. The Marketing column
          indicates whether you have permission to email a user for promotional
          or non-support purposes.
        </Typography>
      </div>
      <SimpleTable
        {...props}
        columns={createColumns()}
        data={data}
        options={{ pageSize: 10 }}
      />
    </div>
  );
}
